import React, { useState, useEffect } from 'react';
import SEO from '../components/common/SEO';
import { projectsAPI } from '../utils/api';
import Filter from '../components/portfolio/Filter';
import ProjectGrid from '../components/portfolio/ProjectGrid';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';
import useScrollAnimation from '../hooks/useScrollAnimation';

const Portfolio = () => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Scroll animations
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        const response = await projectsAPI.getAll();
        const allProjects = response.data.data || [];
        setProjects(allProjects);
        setFilteredProjects(allProjects);
      } catch (err) {
        setError(err.message || 'Failed to load projects');
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  useEffect(() => {
    if (activeFilter === 'all') {
      setFilteredProjects(projects);
    } else {
      setFilteredProjects(
        projects.filter((project) => project.category === activeFilter)
      );
    }
  }, [activeFilter, projects]);

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  return (
    <>
      <SEO
        title="Portfolio - Confident Portraits | Photography & Creative Work"
        description="Explore my portfolio of portrait photography, writing projects, and social media campaigns. View featured work showcasing authentic and empowering portraits."
        url="/portfolio"
      />
      <div className="pt-24 md:pt-28 pb-20 bg-white min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header with fade-in animation */}
          <div
            ref={headerRef}
            className={`text-center mb-16 md:mb-20 transition-all duration-700 ease-out ${
              headerVisible
                ? 'opacity-100 translate-y-0'
                : 'opacity-0 translate-y-8'
            }`}
          >
            <h1 className="text-4xl md:text-5xl lg:text-display font-heading font-bold text-secondary mb-6 leading-tight">
              Portfolio
            </h1>
            <p className="text-lg md:text-xl text-text-secondary font-body max-w-2xl mx-auto leading-relaxed">
              Explore our collection of photography, writing, and social media projects
            </p>
          </div>

        {/* Filter with fade-in */}
        <div
          className={`transition-all duration-700 ease-out ${
            headerVisible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: headerVisible ? '200ms' : '0ms' }}
        >
          <Filter activeFilter={activeFilter} onFilterChange={handleFilterChange} />
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loading size="lg" text="Loading portfolio..." />
          </div>
        ) : error ? (
          <ErrorMessage message={error} className="max-w-2xl mx-auto" />
        ) : (
          <ProjectGrid projects={filteredProjects} />
        )}
        </div>
      </div>
    </>
  );
};

export default Portfolio;
